import * as XLSX from 'xlsx';
import { createPdf } from '@/vendor/pdfMake';

const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

const formatHours = (value: number): string => {
  return String(Math.round(value * 10) / 10);
};

const hasPercentageAndHours = (value: any): boolean => {
  return value && typeof value === 'object' && 'percentage' in value && 'hours' in value;
};

const buildExportHeaders = (columns: any[], list: any[], data: any, scope: any) => {
  const headers: string[] = [];
  const columnMapping: Array<{ column: any; type: 'simple' | 'percentage' | 'hours' }> = [];

  columns.forEach(column => {
    if (!column.title) return;

    const sampleValue = list.length > 0 ? column.lens(list[0], { data }, scope) : null;

    if (hasPercentageAndHours(sampleValue)) {
      headers.push(`${column.title} %`);
      columnMapping.push({ column, type: 'percentage' });

      headers.push(`${column.title} Hours`);
      columnMapping.push({ column, type: 'hours' });
    } else {
      headers.push(column.title);
      columnMapping.push({ column, type: 'simple' });
    }
  });

  return { headers, columnMapping };
};

const formatValueForExport = (value: any, type: 'simple' | 'percentage' | 'hours'): string => {
  if (value == null || value === '') return '';

  if (type === 'percentage' && hasPercentageAndHours(value)) {
    return formatPercentage(value.percentage ?? 0);
  }

  if (type === 'hours' && hasPercentageAndHours(value)) {
    return formatHours(value.hours ?? 0);
  }

  if (typeof value === 'number') {
    return String(Math.round(value * 100) / 100);
  }

  return String(value);
};

export const exportPhysicianCSV = (list: any[], columns: any[], data: any, scope: any, fileName: string) => {
  const { headers, columnMapping } = buildExportHeaders(columns, list, data, scope);

  const json = list
    .filter(_ => _ && !_.separator)
    .map(item => {
      const row: Record<string, string> = {};
      columnMapping.forEach(({ column, type }, index) => {
        const value = column.lens(item, { data }, scope);
        row[headers[index]] = formatValueForExport(value, type);
      });
      return row;
    });

  const csvRows: string[] = [];

  csvRows.push(headers.map(h => `"${h}"`).join(','));

  json.forEach(row => {
    const values = headers.map(header => {
      const value = row[header] || '';
      return `"${String(value).replace(/"/g, '""')}"`;
    });
    csvRows.push(values.join(','));
  });

  const csvContent = csvRows.join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', fileName + '.csv');
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportPhysicianXLSX = (list: any[], columns: any[], data: any, scope: any, fileName: string) => {
  const { headers, columnMapping } = buildExportHeaders(columns, list, data, scope);

  const json = list
    .filter(_ => _ && !_.separator)
    .map(item => {
      const row: Record<string, string> = {};
      columnMapping.forEach(({ column, type }, index) => {
        const value = column.lens(item, { data }, scope);
        row[headers[index]] = formatValueForExport(value, type);
      });
      return row;
    });

  const worksheet = XLSX.utils.json_to_sheet(json);
  XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });

  const colWidths = headers.map(header => {
    const maxLength = Math.max(header.length, ...json.map(row => String(row[header] || '').length));
    return { wch: Math.min(maxLength + 2, 50) };
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  XLSX.writeFile(workbook, fileName + '.xlsx');
};

const formatForPDF = (value: any, type: 'simple' | 'percentage' | 'hours'): string => {
  if (value == null || value === '') return '';

  if (type === 'percentage' && hasPercentageAndHours(value)) {
    return formatPercentage(value.percentage ?? 0);
  }

  if (type === 'hours' && hasPercentageAndHours(value)) {
    return `${formatHours(value.hours ?? 0)}h`;
  }

  if (typeof value === 'number') {
    return String(Math.round(value * 100) / 100);
  }

  return String(value);
};

const chunkArray = (arr: any[], size: number): any[][] => {
  const chunks: any[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

export const exportPhysicianPDF = (list: any[], columns: any[], data: any, scope: any, options: any = {}) => {
  const { headers, columnMapping } = buildExportHeaders(columns, list, data, scope);

  const headerRow = headers;
  const rows = list
    .filter(_ => _ && !_.separator)
    .map(item => {
      return columnMapping.map(({ column, type }) => {
        const value = column.lens(item, { data }, scope);
        const text = formatForPDF(value, type);
        return { text, noWrap: false };
      });
    });

  const body = [headerRow, ...rows];

  const colCount = headers.length;
  const maxColsPerPage = options.maxColsPerPage || 10;
  const freezeCols = Math.min(options.freezeCols || 1, colCount);

  const frozenCols = headers.slice(0, freezeCols);
  const variableCols = headers.slice(freezeCols);
  const perPageVariable = Math.max(1, maxColsPerPage - freezeCols);

  const variableChunks =
    variableCols.length > perPageVariable ? chunkArray(variableCols, perPageVariable) : [variableCols];

  const columnPages = variableChunks.map(chunk => [
    ...Array(frozenCols.length).keys(),
    ...chunk.map((_, i) => i + freezeCols),
  ]);

  const content = columnPages.map((colIndices, pageIdx) => {
    const pageHeaders = colIndices.map(i => headers[i]);
    const pageRows = rows.map(row => colIndices.map(i => row[i]));

    return {
      pageBreak: pageIdx < columnPages.length - 1 ? 'after' : undefined,
      table: {
        headerRows: 1,
        dontBreakRows: true,
        keepWithHeaderRows: 1,
        widths: new Array(pageHeaders.length).fill('auto'),
        body: [pageHeaders, ...pageRows],
      },
      layout: 'lightHorizontalLines',
    };
  });

  const docDefinition = {
    pageOrientation: 'landscape',
    pageSize: 'A4',
    pageMargins: [20, 20, 20, 20],
    content,
    defaultStyle: { fontSize: 9 },
  };

  const pdf = createPdf(docDefinition);
  const mode = options.mode || 'print';

  if (mode === 'download') {
    pdf.download(options.fileName || 'export.pdf');
  } else if (mode === 'open') {
    pdf.open();
  } else {
    pdf.print();
  }
};
